import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '../../shared/api/http'
import { useAuth } from '../auth/AuthContext'
import {
  clearResidentSession, residentApi, residentToken, savedResidentClient,
  saveResidentClient, saveResidentSession, type ResidentProfile, type ResidentSession,
} from './residentApi'
import './portaria.css'
import { InternalTickets } from './InternalTickets'
import { ReservationCalendar } from './ReservationCalendar'
import { ReservationWaitlist } from './ReservationWaitlist'
import { VisitorDirectory } from './VisitorDirectory'
import { CommunityDocuments } from './CommunityDocuments'
import { HouseholdAssets } from './HouseholdAssets'
import { CommunityPolls } from './CommunityPolls'

type ResidentAlert = { id: string; kind?: string; title: string; body: string; createdAt: string; readAt: string | null; deliveryState: string; deliveredAt: string | null }
type ResidentPackage = { id: string; code: string; carrier: string | null; receivedAt: string; collectedAt: string | null; status: string }
type ResidentArea = { id: string; name: string; capacity: number; rules: string | null; minMinutes: number; maxMinutes: number; approvalRequired: boolean; maxReservationsPerUnitMonth: number }
type ResidentReservation = { id: string; areaName: string; startsAt: string; endsAt: string; status: string; guestNames: string[] }
type ResidentVisitor = { id: string; name: string; phone: string | null; validFrom: string; validUntil: string; status: string; enteredAt: string | null; exitedAt: string | null }

const dateText = (value: string) => new Date(value).toLocaleString('pt-BR')

export function PortariaEntryPage() {
  const { user } = useAuth()
  return <main className="portaria-entry">
    <div className="portaria-entry-card">
      <span className="module-choice-eyebrow">SPECTRA PORTARIA</span>
      <h1>Como você vai entrar?</h1>
      <p>Escolha o acesso da equipe ou da sua unidade.</p>
      <div className="portaria-entry-options">
        <Link to={user ? '/portaria' : '/login?modulo=portaria'}><strong>Equipe da portaria</strong><small>Administrador ou operador do condomínio</small><b>Entrar →</b></Link>
        <Link to="/portaria/morador"><strong>Morador</strong><small>Encomendas, avisos e reservas da sua unidade</small><b>Entrar →</b></Link>
      </div>
      <Link className="portaria-entry-back" to="/modulos">← Trocar módulo</Link>
    </div>
  </main>
}

export function ResidentPortariaPage() {
  const navigate = useNavigate()
  const [clientId, setClientId] = useState(savedResidentClient)
  const [inviteCode, setInviteCode] = useState('')
  const [mode, setMode] = useState<'login' | 'activate'>('login')
  const [profile, setProfile] = useState<ResidentProfile | null>(null)
  const [alerts, setAlerts] = useState<ResidentAlert[]>([])
  const [packages, setPackages] = useState<ResidentPackage[]>([])
  const [areas, setAreas] = useState<ResidentArea[]>([])
  const [reservations, setReservations] = useState<ResidentReservation[]>([])
  const [visitors, setVisitors] = useState<ResidentVisitor[]>([])
  const [tab, setTab] = useState<'avisos' | 'encomendas' | 'reservas' | 'visitantes' | 'chamados' | 'documentos' | 'enquetes' | 'veiculos-pets'>('avisos')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function load() {
    const me = await residentApi<ResidentProfile>('me')
    const [nextAlerts, nextPackages, nextAreas, nextReservations, nextVisitors] = await Promise.all([
      residentApi<ResidentAlert[]>('alerts'), residentApi<ResidentPackage[]>('packages'),
      residentApi<ResidentArea[]>('areas'), residentApi<ResidentReservation[]>('reservations'),
      residentApi<ResidentVisitor[]>('visitors'),
    ])
    setProfile(me)
    setClientId(me.clientId)
    saveResidentClient(me.clientId)
    setAlerts(nextAlerts); setPackages(nextPackages)
    setAreas(nextAreas); setReservations(nextReservations); setVisitors(nextVisitors)
  }

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1))
    const invitedClient = fragment.get('cliente')
    const code = fragment.get('convite')
    if (invitedClient) { setClientId(invitedClient); saveResidentClient(invitedClient) }
    if (code) { setInviteCode(code); setMode('activate') }
    if (invitedClient || code) window.history.replaceState(null, '', window.location.pathname + window.location.search)
    if (residentToken()) void load().catch(() => clearResidentSession())
  }, [])

  useEffect(() => {
    if (tab !== 'avisos' || !profile) return
    const pending = alerts.filter(item => item.deliveryState === 'queued')
    if (!pending.length) return
    let active = true
    void Promise.allSettled(pending.map(item => residentApi<void>(`alerts/${item.id}/delivered`, {}, 'POST')))
      .then(results => {
        if (!active) return
        const delivered = new Set(pending.filter((_, index) => results[index].status === 'fulfilled').map(item => item.id))
        if (delivered.size) setAlerts(current => current.map(item => delivered.has(item.id)
          ? { ...item, deliveryState: 'delivered', deliveredAt: new Date().toISOString() } : item))
      })
    return () => { active = false }
  }, [tab, profile, alerts])

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = new FormData(event.currentTarget)
    setBusy(true); setError(''); setMessage('')
    try {
      const session = await residentApi<ResidentSession>('login', {
        clientId, login: String(values.get('login') ?? '').trim(),
        password: String(values.get('password') ?? ''), rememberMe: values.has('remember'),
      })
      saveResidentSession(session, values.has('remember'))
      await load()
    } catch {
      clearResidentSession()
      setError('Acesso não autorizado. Confira condomínio, usuário e senha.')
    } finally { setBusy(false) }
  }

  async function submitActivation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = new FormData(event.currentTarget)
    const password = String(values.get('password') ?? '')
    if (password !== values.get('confirmation')) { setError('As senhas não coincidem.'); return }
    setBusy(true); setError('')
    try {
      await residentApi<void>('activate', {
        clientId, inviteCode, login: String(values.get('login') ?? '').trim(), password,
      })
      setInviteCode(''); setMode('login')
      setMessage('Conta ativada. Entre com o usuário e a senha definidos.')
    } catch (cause) {
      setError(cause instanceof ApiError && cause.code === 'login_in_use'
        ? 'Esse usuário já está em uso no condomínio.'
        : 'Não foi possível ativar o convite. Confira o prazo e o código.')
    } finally { setBusy(false) }
  }

  async function logout() {
    try { await residentApi<void>('logout', {}, 'POST') } catch { /* sessão local será removida */ }
    clearResidentSession(); setProfile(null); setTab('avisos')
  }

  async function readAlert(id: string) {
    try {
      await residentApi<void>(`alerts/${id}/read`, {}, 'POST')
      await load()
    } catch { setError('Não foi possível confirmar a leitura do aviso.') }
  }

  async function reserve(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const values = new FormData(form)
    const areaId = String(values.get('areaId') ?? '')
    setBusy(true); setError(''); setMessage('')
    try {
      await residentApi(`areas/${areaId}/reservations`, {
        startsAt: new Date(String(values.get('startsAt'))).toISOString(),
        endsAt: new Date(String(values.get('endsAt'))).toISOString(),
        guestNames: String(values.get('guestNames') ?? '').split('\n').map(name => name.trim()).filter(Boolean),
        acceptRules: values.get('acceptRules') === 'on',
      })
      form.reset(); await load(); setMessage('Reserva registrada.')
    } catch (cause) {
      setError(cause instanceof ApiError && cause.code === 'slot_unavailable'
        ? 'Esse horário já está reservado.'
        : cause instanceof ApiError && cause.code === 'area_capacity_exceeded'
          ? 'A quantidade de convidados supera a capacidade da área.'
          : cause instanceof ApiError && cause.code === 'unit_monthly_limit'
            ? 'Sua unidade atingiu o limite mensal de reservas desta área.'
        : 'Não foi possível reservar. Confira o horário e as regras da área.')
    } finally { setBusy(false) }
  }

  async function cancelReservation(id: string) {
    setBusy(true); setError(''); setMessage('')
    try {
      await residentApi<void>(`reservations/${id}/cancel`, {}, 'POST')
      await load(); setMessage('Reserva cancelada.')
    } catch { setError('Não foi possível cancelar a reserva.') }
    finally { setBusy(false) }
  }

  async function authorizeVisitor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const values = new FormData(form)
    setBusy(true); setError(''); setMessage('')
    try {
      await residentApi('visitors', {
        name: String(values.get('name') ?? '').trim(),
        phone: String(values.get('phone') ?? '').trim() || null,
        validFrom: new Date(String(values.get('validFrom'))).toISOString(),
        validUntil: new Date(String(values.get('validUntil'))).toISOString(),
      })
      form.reset(); await load(); setMessage('Visita autorizada para sua unidade.')
    } catch { setError('Não foi possível autorizar a visita. Confira o período informado.') }
    finally { setBusy(false) }
  }

  async function revokeVisitor(id: string) {
    setBusy(true); setError(''); setMessage('')
    try {
      await residentApi<void>(`visitors/${id}/revoke`, {}, 'POST')
      await load(); setMessage('Autorização da visita revogada.')
    } catch { setError('Não foi possível revogar esta visita.') }
    finally { setBusy(false) }
  }

  if (!profile) return <main className="portaria-entry"><div className="portaria-entry-card portaria-resident-login">
    <span className="module-choice-eyebrow">SPECTRA PORTARIA</span>
    <h1>{mode === 'activate' ? 'Ativar acesso de morador' : 'Acesso do morador'}</h1>
    <p>{mode === 'activate' ? 'Defina seu usuário e uma senha pessoal.' : 'Entre para acompanhar sua unidade.'}</p>
    {message && <p className="portaria-success" role="status">{message}</p>}
    {error && <p className="portaria-error" role="alert">{error}</p>}
    <form onSubmit={mode === 'activate' ? submitActivation : submitLogin}>
      <label>Código do condomínio<input value={clientId} onChange={event => setClientId(event.target.value)} required placeholder="Abra o link enviado pela portaria" /></label>
      {mode === 'activate' && <label>Código do convite<input value={inviteCode} onChange={event => setInviteCode(event.target.value)} required /></label>}
      <label>Usuário (e-mail ou telefone)<input name="login" autoComplete="username" required maxLength={120} /></label>
      <label>Senha<input type="password" name="password" autoComplete={mode === 'activate' ? 'new-password' : 'current-password'} required minLength={mode === 'activate' ? 12 : undefined} /></label>
      {mode === 'activate' && <label>Confirmar senha<input type="password" name="confirmation" autoComplete="new-password" required /></label>}
      {mode === 'login' && <label className="portaria-check"><input type="checkbox" name="remember" /> Permanecer conectado</label>}
      <button disabled={busy}>{busy ? 'Aguarde…' : mode === 'activate' ? 'Ativar conta' : 'Entrar'}</button>
    </form>
    <button className="portaria-quiet" onClick={() => { setMode(mode === 'login' ? 'activate' : 'login'); setError('') }}>{mode === 'login' ? 'Tenho um convite' : 'Já tenho conta'}</button>
    <Link className="portaria-entry-back" to="/portaria/entrada">← Voltar</Link>
  </div></main>

  return <div className="portaria-page"><header className="portaria-topbar">
    <div className="portaria-brand"><img src="/logo.png" alt="" /><span><strong>Spectra Portaria</strong><small>{profile.name}</small></span></div>
    <div className="portaria-top-actions"><button onClick={() => void logout()}>Sair</button><button onClick={() => navigate('/modulos')}>Módulos</button></div>
  </header><main className="portaria-container">
    <div className="portaria-title"><div><span>ÁREA DO MORADOR</span><h1>{profile.clientName}</h1><p>{profile.blockName ? `${profile.blockName} · ` : ''}{profile.unitLabel}</p></div></div>
    {error && <p className="portaria-error" role="alert">{error}</p>}
    {message && <p className="portaria-success" role="status">{message}</p>}
    <nav className="portaria-tabs">
      {([
        ['avisos', 'Avisos'], ['encomendas', 'Encomendas'], ['reservas', 'Reservas'],
        ['visitantes', 'Visitantes'], ['veiculos-pets', 'Veículos e pets'], ['chamados', 'Chamados'],
        ['documentos', 'Documentos'], ['enquetes', 'Enquetes e assembleias'],
      ] as const).map(([id, label]) => <button key={id} className={tab === id ? 'active' : ''} onClick={() => { setTab(id); setMessage(''); setError('') }}>{label}</button>)}
    </nav>
    <div className="portaria-content">
      {tab === 'chamados' && <InternalTickets clientId={profile.clientId} units={[]} canManage={false} resident />}
      {tab === 'veiculos-pets' && <HouseholdAssets clientId={profile.clientId} resident canManage units={[{id: profile.unitId, label: profile.unitLabel, blockId: null, blockName: profile.blockName, floor: null}]} />}
      {tab === 'documentos' && <CommunityDocuments clientId={profile.clientId} resident />}
      {tab === 'enquetes' && <CommunityPolls clientId={profile.clientId} resident />}
      {tab === 'visitantes' && <VisitorDirectory clientId={profile.clientId} resident onAuthorized={load} />}
      {tab === 'avisos' && <><CommunityDocuments clientId={profile.clientId} resident announcements /><div className="portaria-card portaria-list"><h2>Avisos para você</h2>{alerts.length ? alerts.map(item => <div key={item.id}><strong>{item.title}</strong><small>{dateText(item.createdAt)} · {item.readAt ? 'Lido' : 'Não lido'}</small><p>{item.body}</p>{item.kind === 'plate_regularization' && <button onClick={() => setTab('veiculos-pets')}>Cadastrar veículo</button>}{!item.readAt && <button onClick={() => void readAlert(item.id)}>Marcar como lido</button>}</div>) : <p>Nenhum aviso no momento.</p>}</div></>}
      {tab === 'encomendas' && <div className="portaria-card portaria-list"><h2>Encomendas da unidade</h2>{packages.length ? packages.map(item => <div key={item.id}><strong>{item.code}</strong><small>{item.carrier ?? 'Transportadora não informada'} · {dateText(item.receivedAt)}</small><p>{item.status === 'collected' ? 'Retirada' : 'Aguardando retirada na portaria'}</p></div>) : <p>Nenhuma encomenda recente.</p>}</div>}
      {tab === 'visitantes' && <><form className="portaria-card portaria-form-grid" onSubmit={authorizeVisitor}><h2>Autorizar visitante</h2><label>Nome<input name="name" required maxLength={160} /></label><label>Telefone<input name="phone" type="tel" maxLength={32} /></label><label>Válido de<input name="validFrom" type="datetime-local" required /></label><label>Válido até<input name="validUntil" type="datetime-local" required /></label><button disabled={busy}>Autorizar visita</button></form><div className="portaria-card portaria-list"><h2>Visitas da unidade</h2>{visitors.length ? visitors.map(item => <div key={item.id}><strong>{item.name}</strong><small>{dateText(item.validFrom)} até {dateText(item.validUntil)} · {item.status}</small>{item.enteredAt && <p>Entrada: {dateText(item.enteredAt)}</p>}{item.exitedAt && <p>Saída: {dateText(item.exitedAt)}</p>}{['pending', 'authorized'].includes(item.status) && !item.enteredAt && new Date(item.validUntil).getTime() > Date.now() && <button disabled={busy} onClick={() => void revokeVisitor(item.id)}>Revogar autorização</button>}</div>) : <p>Nenhuma visita recente.</p>}</div></>}
      {tab === 'reservas' && <>
        <ReservationCalendar clientId={profile.clientId} areas={areas} resident />
        <ReservationWaitlist clientId={profile.clientId} areas={areas} resident />
        <form className="portaria-card portaria-form-grid" onSubmit={reserve}>
          <h2>Reservar área comum</h2>
          <label>Área<select name="areaId" required><option value="">Selecione</option>{areas.map(item => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
          <label>Início<input type="datetime-local" name="startsAt" required /></label>
          <label>Fim<input type="datetime-local" name="endsAt" required /></label>
          <label>Convidados (um nome por linha)<textarea name="guestNames" rows={3} placeholder="Deixe vazio se não houver convidados" /></label>
          <label><input name="acceptRules" type="checkbox" required /> Li e aceito as regras da área selecionada.</label>
          <button disabled={busy || !areas.length}>Solicitar reserva</button>
        </form>
        <div className="portaria-card portaria-list"><h2>Reservas da unidade</h2>{reservations.length ? reservations.map(item => <div key={item.id}>
          <strong>{item.areaName}</strong><small>{dateText(item.startsAt)} até {dateText(item.endsAt)} · {item.guestNames.length} convidado(s)</small>
          {item.guestNames.length > 0 && <small>{item.guestNames.join(', ')}</small>}
          <p>{item.status}</p>{['pending', 'approved'].includes(item.status) && new Date(item.startsAt).getTime() > Date.now() && <button disabled={busy} onClick={() => void cancelReservation(item.id)}>Cancelar reserva</button>}
        </div>) : <p>Nenhuma reserva recente.</p>}</div>
        <div className="portaria-card portaria-list"><h2>Regras das áreas</h2>{areas.map(item => <div key={item.id}>
          <strong>{item.name}</strong><small>Capacidade {item.capacity} · {item.minMinutes}–{item.maxMinutes} min · {item.maxReservationsPerUnitMonth ? `${item.maxReservationsPerUnitMonth} por unidade/mês` : 'Sem limite por unidade'}</small>
          <p>{item.rules ?? 'Consulte a portaria para detalhes.'}</p>
        </div>)}</div>
      </>}
    </div>
  </main></div>
}
